import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "../../firebase";
import "./dashboard.css";

/**
 * Live owner dashboard for a single license. Listens to
 * `licenses/{licenseId}/reports/today` via Firestore onSnapshot — sub-second
 * updates as the desktop POS pushes after every settle / void / cancel.
 *
 * Field shape mirrors the legacy owner-dashboard so the rendering code is a
 * straight port. Server-side push is handled by NewLicenseReportPushService
 * on the desktop, which posts to the pushReport Cloud Function.
 */

interface ReportDoc {
  restaurantName?:        string;
  date?:                  string;
  businessDate?:          string;
  totalBills?:            number;
  totalSale?:             number;
  netSales?:              number;
  totalGuests?:           number;
  discountTotal?:         number;
  postTaxDiscountTotal?:  number;
  cgstTotal?:             number;
  sgstTotal?:             number;
  vatTotal?:              number;
  voidBills?:             number;
  voidBillAmount?:        number;
  complementaryBills?:    number;
  complementaryAmount?:   number;
  paymentBreakdown?:      Record<string, number>;
  categoryBreakdown?:     { name: string; qty: number; total: number }[];
  topItems?:              { name: string; qty: number; total: number }[];
  voidBillDetails?:       VoidRow[];
  modifiedBillDetails?:   ModifiedRow[];
  kotCancellations?:      KotRow[];
  /** True totals for the detail arrays — present when the desktop trims the
   *  live-push detail rows to the most recent N. Header reads these instead
   *  of `.length` so the count stays honest even when the array is trimmed. */
  voidBillsTotal?:        number;
  modifiedBillsTotal?:    number;
  kotCancellationsTotal?: number;
  /** Tables with at least one active KOT item right now — pushed live so
   *  the owner can see what's "in service" in near real-time. */
  runningTables?:         RunningTable[];
  lastSync?:              string;
  pushedAt?:              { seconds: number; nanoseconds: number } | { _seconds: number; _nanoseconds: number };
}
interface VoidRow {
  billNumber: string;
  tableNumber: string;
  amount: number;
  reason: string;
  voidedBy: string;
  voidedAt: string;
}
interface ModifiedRow {
  billNumber: string;
  tableNumber: string;
  originalAmount: number;
  newAmount: number;
  reason: string;
}
interface RunningTable {
  billId:      number;
  billNumber:  string;
  tableNumber: string;
  section?:    string;
  waiterName?: string;
  guestCount?: number;
  totalQty:    number;
  totalAmount: number;
  status:      string;
  openSince:   string;
}
interface KotRow {
  kotNumber: string;
  tableNumber: string;
  itemName: string;
  quantity: number;
  reason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
}

const PAY_ICONS: Record<string, string>  = { Cash: "💵", UPI: "📱", Card: "💳", Bank: "🏦" };
const PAY_COLORS: Record<string, string> = { Cash: "#22c55e", UPI: "#3b82f6", Card: "#f59e0b", Bank: "#a78bfa" };

const fmtCurrency = (n: number | undefined | null) =>
  "₹" + Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const fmtNum = (n: number | undefined | null) => Number(n ?? 0).toLocaleString("en-IN");

export function DashboardPage() {
  const { licenseId } = useParams<{ licenseId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ReportDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!licenseId) return;
    let unsub: Unsubscribe | undefined;
    try {
      const ref = doc(db, "licenses", licenseId, "reports", "today");
      unsub = onSnapshot(
        ref,
        snap => {
          setLoaded(true);
          setError(null);
          setData(snap.exists() ? (snap.data() as ReportDoc) : null);
        },
        err => {
          setLoaded(true);
          setError(err.message || "Failed to load report");
        },
      );
    } catch (e) {
      setLoaded(true);
      setError((e as Error).message);
    }
    return () => { unsub?.(); };
  }, [licenseId]);

  const dateLabel = useMemo(() => {
    if (!data?.date && !data?.businessDate) return "—";
    const d = new Date(data.businessDate ?? data.date!);
    return isNaN(d.getTime())
      ? (data.businessDate ?? data.date ?? "—")
      : d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  }, [data]);

  const totalTax = (data?.cgstTotal ?? 0) + (data?.sgstTotal ?? 0) + (data?.vatTotal ?? 0);

  return (
    <div className="od-dashboard">
      <div className="od-header">
        <div>
          <h2>{data?.restaurantName ?? "Loading…"}</h2>
          <p>{dateLabel}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="od-live"><span className="od-live-dot"/>LIVE</span>
          <button className="od-back" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>

      {!loaded && <div className="od-loading">Connecting…</div>}
      {loaded && error && <div className="od-loading" style={{ color: "var(--red)" }}>Error: {error}</div>}
      {loaded && !error && !data && (
        <div className="od-loading">No data synced yet. The POS pushes after every bill is settled.</div>
      )}

      {data && (
        <>
          {/* Total */}
          <div className="od-section">
            <div className="od-total-card">
              <div>
                <div className="od-total-amount">{fmtCurrency(data.totalSale ?? data.netSales)}</div>
                <div className="od-total-label">Total Sale</div>
              </div>
              <div className="od-total-bills">
                <div className="num">{data.totalBills ?? 0}</div>
                <div className="lbl">Bills</div>
              </div>
            </div>
          </div>

          {/* Summary row */}
          <div className="od-section">
            <div className="od-summary-grid">
              <div className="od-summary-item">
                <div className="val">{fmtNum(data.totalGuests)}</div>
                <div className="lbl">Guests</div>
              </div>
              <div className="od-summary-item">
                <div className="val">{fmtCurrency(data.postTaxDiscountTotal || data.discountTotal || 0)}</div>
                <div className="lbl">Discount</div>
              </div>
              <div className="od-summary-item">
                <div className="val">{fmtCurrency(totalTax)}</div>
                <div className="lbl">Tax</div>
              </div>
            </div>
          </div>

          {/* Sales breakdown */}
          {(data.voidBillAmount ?? 0) > 0 || (data.complementaryAmount ?? 0) > 0 ? (
            <div className="od-section">
              <div className="od-mini-card">
                <div className="od-mini-card-title">Sales Breakdown</div>
                <div className="od-sb-row">
                  <span>Total Sale</span>
                  <span>{fmtCurrency(data.totalSale ?? 0)}</span>
                </div>
                {(data.voidBillAmount ?? 0) > 0 && (
                  <div className="od-sb-row">
                    <span>Less: Void</span>
                    <span style={{ color: "var(--red)" }}>−{fmtCurrency(data.voidBillAmount)}</span>
                  </div>
                )}
                {(data.complementaryAmount ?? 0) > 0 && (
                  <div className="od-sb-row">
                    <span>Less: Complementary</span>
                    <span style={{ color: "var(--red)" }}>−{fmtCurrency(data.complementaryAmount)}</span>
                  </div>
                )}
                <div className="od-sb-net">
                  <span>Net Sales</span>
                  <span style={{ color: "var(--green)", fontFamily: "'DM Mono', monospace" }}>
                    {fmtCurrency((data.totalSale ?? 0) - (data.voidBillAmount ?? 0) - (data.complementaryAmount ?? 0))}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          {/* Running tables — live snapshot of what's currently in service */}
          {(data.runningTables?.length ?? 0) > 0 && (
            <div className="od-section">
              <div className="od-section-title">
                Running Tables
                <span style={{ fontSize: 12, fontWeight: 400, color: "var(--text2)", marginLeft: 8 }}>
                  · {data.runningTables!.length} table{data.runningTables!.length === 1 ? "" : "s"} in service
                </span>
              </div>
              <RunningTablesList rows={data.runningTables!} />
            </div>
          )}

          {/* Payment + Categories */}
          <div className="od-section">
            <div className="od-two-col">
              <div className="od-mini-card">
                <div className="od-mini-card-title">Payment</div>
                <PaymentList breakdown={data.paymentBreakdown ?? {}} />
              </div>
              <div className="od-mini-card">
                <div className="od-mini-card-title">Categories</div>
                <CategoryList items={data.categoryBreakdown ?? []} />
              </div>
            </div>
          </div>

          {/* Top items */}
          <div className="od-section">
            <div className="od-section-title">Top Items</div>
            <div className="od-mini-card" style={{ maxHeight: 240, overflowY: "auto" }}>
              <TopItems items={data.topItems ?? []} />
            </div>
          </div>

          {/* Void bills */}
          {(data.voidBillDetails?.length ?? 0) > 0 && (
            <div className="od-section">
              <div className="od-section-title">
                Void Bills ({data.voidBillsTotal ?? data.voidBillDetails!.length}
                {data.voidBillsTotal != null && data.voidBillsTotal > data.voidBillDetails!.length
                  ? ` — showing recent ${data.voidBillDetails!.length}` : ""})
              </div>
              {data.voidBillDetails!.map((v, i) => (
                <div key={i} className="od-list-row">
                  <div className="head">
                    <span className="num">{v.billNumber} <span style={{ color: "var(--text2)", fontWeight: 400 }}>· T{v.tableNumber}</span></span>
                    <span style={{ color: "var(--red)" }}>{fmtCurrency(v.amount)}</span>
                  </div>
                  <div className="meta">{v.reason} · by {v.voidedBy}</div>
                </div>
              ))}
            </div>
          )}

          {/* Modified bills */}
          {(data.modifiedBillDetails?.length ?? 0) > 0 && (
            <div className="od-section">
              <div className="od-section-title">
                Modified Bills ({data.modifiedBillsTotal ?? data.modifiedBillDetails!.length}
                {data.modifiedBillsTotal != null && data.modifiedBillsTotal > data.modifiedBillDetails!.length
                  ? ` — showing recent ${data.modifiedBillDetails!.length}` : ""})
              </div>
              {data.modifiedBillDetails!.map((m, i) => {
                const delta = m.newAmount - m.originalAmount;
                return (
                  <div key={i} className="od-list-row">
                    <div className="head">
                      <span className="num">{m.billNumber} <span style={{ color: "var(--text2)", fontWeight: 400 }}>· T{m.tableNumber}</span></span>
                      <span style={{ color: delta < 0 ? "var(--red)" : "var(--green)" }}>
                        {delta >= 0 ? "+" : ""}{fmtCurrency(delta)}
                      </span>
                    </div>
                    <div className="meta">
                      {fmtCurrency(m.originalAmount)} → {fmtCurrency(m.newAmount)} · {m.reason}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* KOT cancellations */}
          {(data.kotCancellations?.length ?? 0) > 0 && (
            <div className="od-section">
              <div className="od-section-title">
                KOT Cancellations ({data.kotCancellationsTotal ?? data.kotCancellations!.length}
                {data.kotCancellationsTotal != null && data.kotCancellationsTotal > data.kotCancellations!.length
                  ? ` — showing recent ${data.kotCancellations!.length}` : ""})
              </div>
              {data.kotCancellations!.map((k, i) => (
                <div key={i} className="od-list-row">
                  <div className="head">
                    <span>{k.itemName} <span style={{ color: "var(--text2)", fontWeight: 400 }}>x{k.quantity}</span></span>
                    <span className="num" style={{ color: "var(--text2)" }}>{k.kotNumber}</span>
                  </div>
                  <div className="meta">
                    T{k.tableNumber}{k.reason ? ` · ${k.reason}` : ""}{k.cancelledBy ? ` · by ${k.cancelledBy}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="od-last-sync">
            {data.lastSync
              ? `Last sync · ${new Date(data.lastSync).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
              : "—"}
          </div>
        </>
      )}
    </div>
  );
}

function PaymentList({ breakdown }: { breakdown: Record<string, number> }) {
  const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
  const total   = entries.reduce((s, [, v]) => s + v, 0) || 1;

  if (entries.length === 0) return <div className="od-empty">No payments yet</div>;

  return (
    <>
      {entries.map(([mode, amount]) => (
        <div key={mode} className="od-pay-row">
          <div className="od-pay-left">
            <span className="od-pay-icon">{PAY_ICONS[mode] ?? "💰"}</span>
            {mode}
          </div>
          <div className="od-pay-amount">{fmtCurrency(amount)}</div>
        </div>
      ))}
      <div className="od-pay-bar-wrap">
        {entries.map(([mode, amount]) => (
          <div
            key={mode}
            className="od-pay-bar"
            style={{ width: `${(amount / total) * 100}%`, background: PAY_COLORS[mode] ?? "#888" }}
          />
        ))}
      </div>
    </>
  );
}

function CategoryList({ items }: { items: { name: string; qty: number; total: number }[] }) {
  if (items.length === 0) return <div className="od-empty">No items yet</div>;
  const sorted = [...items].sort((a, b) => b.total - a.total).slice(0, 6);
  return (
    <>
      {sorted.map((c, i) => (
        <div key={i} className="od-cat-row">
          <div className="od-cat-name">{c.name}</div>
          <div className="od-cat-val">
            <span className="amt">{fmtCurrency(c.total)}</span>
            <span className="cnt">{c.qty} qty</span>
          </div>
        </div>
      ))}
    </>
  );
}

function TopItems({ items }: { items: { name: string; qty: number; total: number }[] }) {
  if (items.length === 0) return <div className="od-empty">No items yet</div>;
  return (
    <>
      {items.slice(0, 10).map((t, i) => (
        <div key={i} className="od-top-row">
          <span className="od-top-rank">{i + 1}.</span>
          <span className="od-top-name">{t.name}</span>
          <span className="od-top-qty">x{t.qty}</span>
          <span className="od-top-total">{fmtCurrency(t.total)}</span>
        </div>
      ))}
    </>
  );
}

/**
 * Running Tables — sorted by openSince so the longest-running table sits on
 * top. Printed (bill generated, awaiting settle) is highlighted in amber so
 * the owner spots tables stuck waiting for payment.
 */
function RunningTablesList({ rows }: { rows: RunningTable[] }) {
  // Newest first feels weird here — owners want to spot the longest-running
  // tables (potential overstays / forgotten bills). Sort ascending by openSince.
  const sorted = [...rows].sort((a, b) =>
    new Date(a.openSince).getTime() - new Date(b.openSince).getTime());

  const fmtElapsed = (iso: string) => {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000)        return `${Math.floor(ms / 1000)}s`;
    if (ms < 60 * 60_000)   return `${Math.floor(ms / 60_000)}m`;
    const h = Math.floor(ms / (60 * 60_000));
    const m = Math.floor((ms % (60 * 60_000)) / 60_000);
    return `${h}h ${m}m`;
  };

  return (
    <div className="od-mini-card" style={{ padding: 0 }}>
      {sorted.map(t => {
        const printed = t.status === "Printed";
        return (
          <div key={t.billId}
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto",
              gap: 10, padding: "10px 12px",
              borderBottom: "1px solid var(--border)",
              background: printed ? "rgba(245, 158, 11, 0.08)" : "transparent",
            }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>
              {t.tableNumber}
              {t.section && <div style={{ fontSize: 10, fontWeight: 400, color: "var(--text2)" }}>{t.section}</div>}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {t.waiterName ? `Waiter: ${t.waiterName}` : "—"}
                {t.guestCount ? <span style={{ color: "var(--text2)", fontWeight: 400, marginLeft: 6 }}>· {t.guestCount} pax</span> : null}
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>
                Open {fmtElapsed(t.openSince)} · Bill {t.billNumber || "—"}
                {printed && <span style={{ color: "var(--amber, #d97706)", marginLeft: 6, fontWeight: 700 }}>· printed, awaiting settle</span>}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--text2)" }}>Items</div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{t.totalQty}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--text2)" }}>Total</div>
              <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'DM Mono', monospace" }}>{fmtCurrency(t.totalAmount)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
